import {
  Fragment,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useBrand } from '@core/contexts/BrandContext';
import { createBrandTheme } from '@core/theme/theme';
import {
  type JobOrder,
  type JobOrderPrintHandle,
  waitForImages,
} from '../../utils/jobOrderPrint';
import CutGuide from './CutGuide';
import JobOrderCard from './JobOrderCard';

interface JobOrderPrintProps {
  jobOrders: JobOrder[];
}

const ITEMS_PER_PAGE = 3;

const CARD_GAP = '0.4in';

const chunk = <T,>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, index * size + size)
  );

const JobOrderPrint = forwardRef<JobOrderPrintHandle, JobOrderPrintProps>(
  ({ jobOrders }, ref) => {
    const { colors } = useBrand();
    const containerRef = useRef<HTMLDivElement>(null);

    const printTheme = useMemo(
      () => createBrandTheme(colors, 'light'),
      [colors]
    );

    useImperativeHandle(
      ref,
      () => ({
        whenReady: async () => {
          if (containerRef.current) {
            await waitForImages(containerRef.current);
          }
        },
      }),
      []
    );

    const pages = useMemo(() => chunk(jobOrders, ITEMS_PER_PAGE), [jobOrders]);

    return (
      <MuiThemeProvider theme={printTheme}>
        <Box ref={containerRef} className="print-only" aria-hidden>
          {pages.map((pageJobOrders, pageIndex) => (
            <Box
              key={pageIndex}
              component="section"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '1150px',
                height: '100vh',
                p: '0.5in',
                boxSizing: 'border-box',
                breakInside: 'avoid',
                breakAfter: pageIndex === pages.length - 1 ? 'auto' : 'page',
              }}
            >
              {Array.from({ length: ITEMS_PER_PAGE }, (_, slot) => {
                const jobOrder = pageJobOrders[slot];
                return (
                  <Fragment key={jobOrder?.item.id ?? `spacer-${slot}`}>
                    {slot > 0 ? (
                      <CutGuide
                        height={CARD_GAP}
                        showLine={slot < pageJobOrders.length}
                      />
                    ) : null}
                    {jobOrder ? (
                      <JobOrderCard
                        order={jobOrder.order}
                        item={jobOrder.item}
                      />
                    ) : (
                      <Box aria-hidden sx={{ flex: 1, minHeight: 0 }} />
                    )}
                  </Fragment>
                );
              })}
            </Box>
          ))}
        </Box>
      </MuiThemeProvider>
    );
  }
);

export default JobOrderPrint;
